import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import {
  CalendarCheck,
  Eye,
  EyeOff,
  KeyRound,
  Lock,
  Mail,
  ShieldCheck,
  Stethoscope,
  User,
  UserCog,
} from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { useSession } from '@/app/session';
import { DEMO_VIEWS, suggestedAccount } from '@/app/demo-view';
import { ApiError } from '@/api/client';
import './LoginPage.css';
import { formatPhone } from '@/lib/format';
import { useTranslation } from 'react-i18next';
import type { TenantDemoAccount } from '@/api/types';
import { BrandMark } from './BrandMark';

/** Iconița fiecărui rol demo: rolul se recunoaște înainte să fie citit. */
function roleIcon(a: TenantDemoAccount) {
  if (a.kind === 'patient') return <User size={15} aria-hidden />;
  if (a.role === 'admin') return <UserCog size={15} aria-hidden />;
  if (a.role === 'medic') return <Stethoscope size={15} aria-hidden />;
  return <CalendarCheck size={15} aria-hidden />;
}

/** Fără animații pentru cine a cerut mișcare redusă. */
const reducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

/**
 * Sign-in.
 *
 * Two-factor is asked for in a second step rather than as an always-visible
 * field: most accounts do not have it, and an empty box captioned
 * "authenticator code" is a question most users cannot answer.
 */
export function LoginPage() {
  const { t } = useTranslation('auth');
  const { tenant, login, demoView, setDemoView } = useSession();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [needsMfa, setNeedsMfa] = useState(false);
  /*
   * Codul de rezervă, pentru telefonul care nu e aici (SEC-5).
   *
   * Al doilea pas n-avea nicio ieșire: cine își pierdea telefonul rămânea pe
   * ecranul ăsta până când un administrator îi resetă contul. Câmpul e același
   * drum — serverul primește ori șase cifre, ori un cod de rezervă — doar că
   * ce se tastează arată altfel, deci și eticheta trebuie să arate altfel.
   */
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  /** Rolul demo apăsat: rotița stă pe butonul lui, nu pe toate patru. */
  const [pendingDemo, setPendingDemo] = useState<string | null>(null);

  const mfaRef = useRef<HTMLInputElement>(null);
  const pageRef = useRef<HTMLElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Lumina de pe fundal urmează cursorul. Se scrie direct în două variabile CSS,
  // o dată pe cadru — o stare React ar fi redesenat formularul la fiecare
  // mișcare a mouse-ului.
  useEffect(() => {
    const page = pageRef.current;
    if (!page || reducedMotion()) return;
    let frame = 0;
    const onMove = (e: PointerEvent) => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const r = page.getBoundingClientRect();
        page.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
        page.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
      });
    };
    page.addEventListener('pointermove', onMove);
    return () => {
      page.removeEventListener('pointermove', onMove);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  // O parolă greșită clatină scurt cardul. Direct din Web Animations, nu dintr-o
  // cheie React: remontarea cardului ar fi mutat focusul înapoi pe email.
  const fail = (message: string) => {
    setError(message);
    if (reducedMotion()) return;
    panelRef.current?.animate?.(
      [
        { transform: 'translateX(0)' },
        { transform: 'translateX(-8px)' },
        { transform: 'translateX(7px)' },
        { transform: 'translateX(-4px)' },
        { transform: 'translateX(0)' },
      ],
      { duration: 360, easing: 'ease-out' },
    );
  };

  const watchCaps = (e: KeyboardEvent<HTMLInputElement>) =>
    setCapsLock(e.getModifierState?.('CapsLock') === true);

  useEffect(() => {
    if (needsMfa) mfaRef.current?.focus();
  }, [needsMfa]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email.trim(), password, needsMfa ? mfaCode.trim() : undefined);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'MfaRequired') {
        // Not an error the user needs to read — it is the second step.
        setNeedsMfa(true);
        setError(null);
      } else if (err instanceof ApiError) {
        fail(err.message);
        // A wrong code should not force the password to be retyped.
        if (needsMfa) setMfaCode('');
      } else {
        fail(t('nuSaPututConexiunea'));
      }
    } finally {
      setBusy(false);
    }
  }

  const clinicName = tenant?.branding.displayName ?? tenant?.name ?? 'DentFlow';

  // Only ever set by the API, and only for a clinic whose own configuration
  // declares it. The browser makes no decision here — it renders what it was
  // given, and for every real clinic it is given null.
  const demo = tenant?.demo ?? null;

  /*
   * Contul pe care vederea aleasă îl pune întâi.
   *
   * Nu reordonează butoanele — un rând care se rearanjează sub degetul omului
   * e mai rău decât unul pe care scrie care e recomandat. Doar îl marchează.
   */
  const suggested = suggestedAccount(demoView, demo?.accounts ?? []);

  async function signInAsDemo(account: { email: string; password: string }) {
    setError(null);
    setBusy(true);
    setPendingDemo(account.email);
    // Filled in as well as submitted, so it is visible what was used — and so
    // a failure leaves the form ready to retry by hand.
    setEmail(account.email);
    setPassword(account.password);
    try {
      await login(account.email, account.password);
    } catch (err) {
      // Un server care nu răspunde nu e un cont indisponibil. Mesajul de
      // dinainte dădea vina pe cont ori de câte ori cererea nu ajungea
      // nicăieri, și trimitea omul să caute exact unde nu era nimic stricat
      // — formularul de mai jos face deja distincția asta.
      fail(err instanceof ApiError ? err.message : t('nuSaPututConexiuneaServer'));
    } finally {
      setBusy(false);
      setPendingDemo(null);
    }
  }

  return (
    <main className="login" ref={pageRef}>
      <span className="login__orb login__orb--a" aria-hidden />
      <span className="login__orb login__orb--b" aria-hidden />
      <div className="login__shell">
        {/* Numele clinicii stă peste degradeu, nu în card — ca titlul de pe
            dentflow.md. Cardul rămâne pentru ce se atinge. */}
        <div className="login__brand">
          <BrandMark name={clinicName} logoUrl={tenant?.branding.logoUrl} />
          <h1 className="login__title">{clinicName}</h1>
          <p className="login__subtitle">
            {needsMfa ? t('verificareInDoiPasi') : t('autentificare')}
          </p>
        </div>

        <div className="login__panel" ref={panelRef}>
          {demo && demo.accounts.length > 0 && !needsMfa ? (
            <section className="login__demo" aria-label={t('conturiDemonstrative')}>
              <div className="login__demo-bar">
                <span className="login__demo-label">Demo</span>
                <span className="login__demo-hint">{demo.note ?? t('alegeUnRol')}</span>
              </div>

              {/* Întrebarea dinaintea autentificării: ce fel de cabinet e, ca
                  să nu i se dea unui medic singur un meniu de clinică cu
                  laborator și resurse umane. Nu blochează intrarea — cine vrea
                  doar înăuntru apasă un rol și merge mai departe; răspunsul
                  schimbă doar ce se vede, și se poate schimba din bara de sus.
                  Vezi `app/demo-view.ts`. */}
              {demoView ? (
                <p className="login__view login__view--set">
                  <span className="login__view-set">
                    {t('common:demoVedere')}: <strong>{t(demoView.labelKey)}</strong>
                  </span>
                  <button
                    type="button"
                    className="login__view-change"
                    onClick={() => setDemoView(null)}
                  >
                    {t('common:demoSchimba')}
                  </button>
                </p>
              ) : (
                <div className="login__view">
                  <p className="login__view-q">{t('ceFelDeCabinetAveti')}</p>
                  <div className="login__view-opts">
                    {DEMO_VIEWS.map((v) => (
                      <button
                        key={v.key}
                        type="button"
                        className="login__view-btn"
                        onClick={() => setDemoView(v.key)}
                      >
                        <span className="login__view-name">{t(v.labelKey)}</span>
                        <span className="login__view-hint">{t(v.hintKey)}</span>
                      </button>
                    ))}
                  </div>
                  <p className="login__view-note">{t('asaStimCeSaAratam')}</p>
                </div>
              )}

              {/* One compact button per role. The credentials are what makes
                this a demo, but they are not what anyone wants to look at:
                the button signs you in, and the text is there for whoever
                needs to type it somewhere else. Hence the disclosure. */}
              <div className="login__demo-roles">
                {demo.accounts.map((a) => (
                  <button
                    key={a.email}
                    type="button"
                    className={[
                      'login__demo-role-btn',
                      suggested?.email === a.email ? 'login__demo-role-btn--suggested' : '',
                    ]
                      .join(' ')
                      .trim()}
                    onClick={() => void signInAsDemo(a)}
                    disabled={busy}
                    title={`${a.description ?? a.label} — ${a.email}`}
                    aria-busy={pendingDemo === a.email || undefined}
                  >
                    {a.label}
                    <span className="login__demo-icon">
                      {pendingDemo === a.email ? (
                        <span className="login__demo-spin" aria-hidden />
                      ) : (
                        roleIcon(a)
                      )}
                    </span>
                    {a.kind === 'patient' ? <span className="login__demo-dot" aria-hidden /> : null}
                  </button>
                ))}
              </div>

              {/* Recomandarea stă sub grilă, nu în buton: butoanele sunt pe două
                  coloane într-un card îngust și taie deja „Administrator" —
                  un cuvânt în plus înăuntru le-ar tăia pe toate. */}
              {suggested ? (
                <p className="login__demo-sug">
                  {t('recomandat')}: <strong>{suggested.label}</strong>
                </p>
              ) : null}

              <details className="login__demo-details">
                <summary>{t('dateleDeAcces')}</summary>
                <ul className="login__demo-list">
                  {demo.accounts.map((a) => (
                    <li key={a.email}>
                      <span className="login__demo-who">{a.label}</span>
                      <code>{a.email}</code>
                      <code>{a.password}</code>
                    </li>
                  ))}
                </ul>
              </details>
            </section>
          ) : null}

          <form className="login__form" onSubmit={onSubmit} noValidate>
            {!needsMfa ? (
              <>
                <Input
                  label="Email"
                  type="email"
                  name="email"
                  autoComplete="username"
                  autoFocus
                  required
                  icon={<Mail size={16} />}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('numeClinicaMd')}
                />
                <Input
                  label={t('parola')}
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  autoComplete="current-password"
                  required
                  icon={<Lock size={16} />}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={watchCaps}
                  onKeyUp={watchCaps}
                  onBlur={() => setCapsLock(false)}
                  hint={capsLock ? t('capsLockPornit') : undefined}
                  suffix={
                    <button
                      type="button"
                      className="login__peek"
                      aria-label={showPassword ? t('ascundeParola') : t('arataParola')}
                      title={showPassword ? t('ascundeParola') : t('arataParola')}
                      aria-pressed={showPassword}
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  }
                />
              </>
            ) : useRecovery ? (
              <Input
                ref={mfaRef}
                label={t('codDeRezerva')}
                name="recovery-code"
                autoComplete="one-time-code"
                autoCapitalize="characters"
                spellCheck={false}
                maxLength={19}
                required
                icon={<ShieldCheck size={16} />}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.toUpperCase())}
                hint={t('unulDintreCodurilePrimite')}
              />
            ) : (
              <Input
                ref={mfaRef}
                label={t('codDinAplicatiaDe')}
                name="one-time-code"
                // These attributes are what let a phone offer the code from the
                // SMS or authenticator app rather than making it be typed.
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                required
                icon={<ShieldCheck size={16} />}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                hint={t('saseCifreDinGoogle')}
              />
            )}

            {error ? (
              <p className="login__error" role="alert">
                {error}
              </p>
            ) : null}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={busy}
              icon={<KeyRound size={16} />}
            >
              {needsMfa ? t('verifica') : t('intraInCont')}
            </Button>

            {needsMfa ? (
              <>
                <button
                  type="button"
                  className="login__back"
                  onClick={() => {
                    setUseRecovery((on) => !on);
                    setMfaCode('');
                    setError(null);
                  }}
                >
                  {useRecovery ? t('amTelefonul') : t('nuAmTelefonul')}
                </button>
                <button
                  type="button"
                  className="login__back"
                  onClick={() => {
                    setNeedsMfa(false);
                    setUseRecovery(false);
                    setMfaCode('');
                    setError(null);
                  }}
                >
                  {t('inapoi')}
                </button>
              </>
            ) : null}
          </form>

          {tenant?.branding.supportPhone || tenant?.branding.supportEmail ? (
            <p className="login__support">
              {t('problemeLaAutentificare')}{' '}
              {tenant.branding.supportPhone ? (
                <a href={`tel:${tenant.branding.supportPhone}`}>
                  {formatPhone(tenant.branding.supportPhone)}
                </a>
              ) : (
                <a href={`mailto:${tenant.branding.supportEmail}`}>
                  {tenant.branding.supportEmail}
                </a>
              )}
            </p>
          ) : null}
        </div>
      </div>
    </main>
  );
}
